import { existsSync } from 'node:fs';
import { Implements, Service, ServiceLifetime } from '@gitlab/needle';
import { Logger, withPrefix } from '@gitlab-org/logging';
import { ConfigService } from '@gitlab-org/config';
import { isFileSchemeUri, parseURIString } from '@gitlab-org/fs';
import { PluginSlashCommandResolver, type PluginSlashCommand } from '@gitlab-org/ai-context/node';
import { doNotAwait } from '@gitlab-org/core';
import {
  CLI_INPUT_TYPES,
  defaultInputState,
  PluginCommandApprovalInput,
  pluginCommandApprovalFooterHint,
  type PluginCommandApprovalCallbacks,
  type PluginCommandApprovalInputState,
} from '@gitlab-org/tui';
import type { ControllerApi } from '../../commands/tui/controller_api';
import { UserActionType } from '../../backend/backend';
import {
  SlashCommandHandler,
  SlashCommandAction,
  type CommandComponentEntry,
} from '../slash_command_handler';
import { PluginCommandRunner } from '../plugin_command_runner';
import { computeApprovalHash, PluginCommandApprovalStore } from '../plugin_command_approval_store';

const OUTPUT_PLACEHOLDER = '{{output}}';
const ARGS_PLACEHOLDER = '{{args}}';
const STDERR_SNIPPET_LENGTH = 500;
const RUN_IN_FLIGHT_MESSAGE = 'Another plugin command is still running; wait for it to finish.';

/**
 * Strips C0/C1 control characters (keeping `\n` and `\t`) so a crafted `run`
 * or env value cannot use escape sequences to render as something other than
 * what it executes. Display-only: execution always uses the raw values.
 */
function sanitizeForDisplay(value: string): string {
  // eslint-disable-next-line no-control-regex
  return value.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g, '');
}

/**
 * Executes slash commands contributed by installed marketplace plugins.
 *
 * Two command shapes are supported:
 * - prompt-only: the markdown body becomes the prompt (custom-command semantics)
 * - `run:` commands: a plugin-declared shell command is executed first and its
 *   output is substituted for `{{output}}` in the body before the prompt is sent
 *
 * `run:` commands are gated behind user approval: the first invocation opens an
 * inline Approve/Reject dialog showing the exact command. Approving persists
 * the approval (keyed by plugin + command, hashed over the command's execution
 * surface), so it survives restarts but re-prompts whenever a plugin update
 * changes the command. Rejecting persists nothing and runs nothing.
 */
@Implements(SlashCommandHandler)
@Service({
  dependencies: [
    Logger,
    PluginSlashCommandResolver,
    PluginCommandRunner,
    ConfigService,
    PluginCommandApprovalStore,
  ],
  lifetime: ServiceLifetime.Singleton,
})
export class DefaultPluginCommandHandler implements SlashCommandHandler {
  #logger: Logger;

  #resolver: PluginSlashCommandResolver;

  #runner: PluginCommandRunner;

  #configService: ConfigService;

  #approvalStore: PluginCommandApprovalStore;

  /**
   * The command whose approval dialog is currently open, captured so the
   * dialog callbacks can run it without re-resolving.
   */
  #pendingRun:
    | { command: PluginSlashCommand; commandArgs: string[]; approvalKey: string; hash: string }
    | undefined;

  /**
   * True while a `run:` command is executing. Plugin runs are not cancellable
   * yet, so a second concurrent run is refused rather than raced.
   */
  #running = false;

  constructor(
    logger: Logger,
    resolver: PluginSlashCommandResolver,
    runner: PluginCommandRunner,
    configService: ConfigService,
    approvalStore: PluginCommandApprovalStore,
  ) {
    this.#logger = withPrefix(logger, '[PluginCommandHandler]');
    this.#resolver = resolver;
    this.#runner = runner;
    this.#configService = configService;
    this.#approvalStore = approvalStore;
  }

  // Placeholder command: every plugin command routes to this single handler
  // via SlashCommandAction.PluginCommand. Marked internal so it never
  // surfaces in the command list or autocomplete.
  command = {
    name: '/plugin-command',
    description: 'Run a plugin-provided slash command.',
    action: SlashCommandAction.PluginCommand,
    internal: true,
  } as const;

  async toPrompt(args?: string[]): Promise<string | undefined> {
    const command = await this.#resolveCommand(args);
    if (!command) return undefined;

    if (command.run !== undefined) {
      // FIXME: POC — headless execution of `run:` commands is unsupported
      // because the approval flow is interactive-only.
      this.#logger.warn(
        `Plugin command "${this.#displayName(command)}" has a run step and cannot be used headlessly yet`,
      );
      return undefined;
    }

    return this.#buildPrompt(command, args?.slice(1) ?? []);
  }

  async execute(api: ControllerApi, args?: string[]): Promise<void> {
    const command = await this.#resolveCommand(args);
    if (!command) {
      this.#logger.warn(`Plugin command "${args?.[0] ?? ''}" not found`);
      return;
    }

    const commandArgs = args?.slice(1) ?? [];
    this.#logger.info(
      `Executing plugin command: ${this.#displayName(command)} (${command.pluginId})`,
    );

    const { run } = command;
    if (run === undefined) {
      await api.sendPrompt({
        type: UserActionType.SendPrompt,
        prompt: this.#buildPrompt(command, commandArgs),
      });
      return;
    }

    if (this.#running) {
      api.showError(RUN_IN_FLIGHT_MESSAGE);
      return;
    }

    // Percent-encoding keeps `/` a boundary neither field can forge, so two
    // different commands can never collapse onto one approval key.
    const approvalKey = `${encodeURIComponent(command.pluginId)}/${encodeURIComponent(command.name)}`;
    const hash = computeApprovalHash({
      run,
      interactive: command.interactive,
      timeoutSeconds: command.timeoutSeconds,
      env: command.env,
    });

    if (await this.#approvalStore.isApproved(approvalKey, hash)) {
      await this.#runAndSend(api, command, commandArgs);
      return;
    }

    this.#pendingRun = { command, commandArgs, approvalKey, hash };
    api.mutateState((state) => ({
      ...state,
      input: {
        inputType: CLI_INPUT_TYPES.PLUGIN_COMMAND_APPROVAL,
        commandName: this.#displayName(command),
        pluginId: command.pluginId,
        run: sanitizeForDisplay(run),
        envLines: Object.entries(command.env ?? {}).map(
          ([key, value]) => `${sanitizeForDisplay(key)}=${sanitizeForDisplay(value)}`,
        ),
      } satisfies PluginCommandApprovalInputState,
    }));
  }

  getComponent(api: ControllerApi): CommandComponentEntry<PluginCommandApprovalCallbacks> {
    return {
      inputType: CLI_INPUT_TYPES.PLUGIN_COMMAND_APPROVAL,
      component: PluginCommandApprovalInput,
      footerHint: pluginCommandApprovalFooterHint,
      callbacks: {
        onApprove: () => doNotAwait(this.#handleApprove(api)),
        onReject: () => this.#handleReject(api),
      },
    };
  }

  async #handleApprove(api: ControllerApi): Promise<void> {
    this.#restoreTextInput(api);
    const pending = this.#pendingRun;
    this.#pendingRun = undefined;
    if (!pending) return;

    try {
      await this.#approvalStore.approve(pending.approvalKey, pending.hash);
    } catch (error) {
      // The user approved this run explicitly; failing to remember the
      // decision only means the next invocation prompts again.
      this.#logger.warn('Could not persist the plugin command approval', this.#asError(error));
    }

    await this.#runAndSend(api, pending.command, pending.commandArgs);
  }

  #handleReject(api: ControllerApi): void {
    this.#restoreTextInput(api);
    const pending = this.#pendingRun;
    this.#pendingRun = undefined;
    if (!pending) return;

    // Nothing is persisted on reject: the next invocation prompts again.
    api.showInfo(`/${this.#displayName(pending.command)} was not approved; nothing was run.`);
  }

  #restoreTextInput(api: ControllerApi): void {
    api.mutateState((state) => ({ ...state, input: defaultInputState }));
  }

  /** Canonical, namespaced name as the user sees it: `<pluginName>:<name>`. */
  #displayName(command: PluginSlashCommand): string {
    return `${command.pluginName}:${command.name}`;
  }

  async #resolveCommand(args?: string[]): Promise<PluginSlashCommand | undefined> {
    const commandName = args?.[0];
    if (!commandName) {
      this.#logger.warn('Plugin command invoked without a name');
      return undefined;
    }
    const { commands } = await this.#resolver.getPluginSlashCommands();
    // args[0] is the registered command name minus its leading `/` — the
    // canonical `<pluginName>:<name>` form, since aliases are resolved before
    // dispatch. Command names cannot contain `:`, so the bare-name fallback
    // (for a directly registered bare name) stays unambiguous.
    return (
      commands.find((c) => `${c.pluginName}:${c.name}` === commandName) ??
      commands.find((c) => c.name === commandName)
    );
  }

  /**
   * Refuses a second run while a plugin process is in flight: runs are not
   * cancellable yet, and an interactive one owns the terminal. The guard
   * covers only the spawned process — the prompt that follows is an ordinary
   * agent turn and must not block the next command.
   */
  async #runAndSend(
    api: ControllerApi,
    command: PluginSlashCommand,
    commandArgs: string[],
  ): Promise<void> {
    if (this.#running) {
      api.showError(RUN_IN_FLIGHT_MESSAGE);
      return;
    }

    const run = command.run as string;
    const name = this.#displayName(command);
    const shownRun = sanitizeForDisplay(run);
    api.showInfo(command.interactive ? `Launching: ${shownRun}` : `Running: ${shownRun}`);

    const cwd = this.#cwd();
    let result;
    this.#running = true;
    try {
      result = await this.#runner.run(run, commandArgs, {
        cwd,
        timeoutSeconds: command.timeoutSeconds,
        interactive: command.interactive,
        env: command.env,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.#logger.warn(`Plugin command "${name}" failed to start`, this.#asError(error));
      if ((error as NodeJS.ErrnoException).code === 'ENOENT') {
        // The runner spawns /bin/sh, which is effectively always present, so
        // ENOENT from spawn almost always means the working directory is gone
        // (a missing wrapped tool surfaces as exit code 127 below instead).
        if (!existsSync(cwd)) {
          api.showError(
            `Plugin command /${name} failed: the working directory does not exist: ${cwd}`,
          );
          return;
        }
        api.showError(
          `Plugin command /${name} failed: the command's executable was not found. Is it installed and on your PATH?`,
        );
        return;
      }
      api.showError(`Plugin command /${name} failed: ${message}`);
      return;
    } finally {
      this.#running = false;
    }

    if (result.timedOut) {
      api.showError(`Plugin command /${name} timed out.`);
      return;
    }

    // A null exit code means the command was killed by a signal (external
    // kill, crash) — distinct from exiting with a failure code.
    if (result.exitCode === null) {
      api.showError(
        `Plugin command /${name} was terminated${
          result.signal ? ` by signal ${result.signal}` : ''
        } before completing.`,
      );
      return;
    }

    // A command may declare additional success codes — e.g. revdiff uses exit
    // code 10 to signal "annotations were produced".
    const successExitCodes = command.successExitCodes ?? [0];
    if (!successExitCodes.includes(result.exitCode)) {
      // 127 is the shell's "command not found" — the most common failure for
      // plugin commands wrapping an external tool the user has not installed.
      if (result.exitCode === 127) {
        api.showError(
          `Plugin command /${name} failed: command not found. Is the tool it wraps installed and on your PATH?`,
        );
        return;
      }
      const snippet = sanitizeForDisplay(result.stderr.trim().slice(0, STDERR_SNIPPET_LENGTH));
      api.showError(
        `Plugin command /${name} exited with code ${result.exitCode}${
          snippet ? `:\n${snippet}` : '.'
        }`,
      );
      return;
    }

    // An empty result only aborts the prompt when the body asks for the
    // output. A body without `{{output}}` is a side-effect command (the run
    // did the work), so it still submits on success.
    if (!result.output.trim() && command.body.includes(OUTPUT_PLACEHOLDER)) {
      api.showInfo(`/${name} finished without producing output; nothing to send.`);
      return;
    }

    let { output } = result;
    if (result.truncated) {
      output += '\n[output truncated]';
    }

    await api.sendPrompt({
      type: UserActionType.SendPrompt,
      prompt: this.#buildPrompt(command, commandArgs, output),
    });
  }

  #buildPrompt(command: PluginSlashCommand, commandArgs: string[], output?: string): string {
    const argsString = commandArgs.join(' ');
    // Replacer callbacks throughout: args and output are untrusted, and a
    // plain string replacement would interpret `$` sequences in them ($&, $',
    // $$, …) as replacement patterns, silently corrupting the prompt.
    const prompt = command.body.replaceAll(ARGS_PLACEHOLDER, () => argsString);

    if (output !== undefined) {
      // FIXME: POC — CLI-enforced injection hardening (random-suffix envelope
      // + boundary notice) was implemented and rolled back: both wordings made
      // the agent refuse to act on the output as task input. Until a wording
      // is validated end-to-end, injection framing is the plugin template's
      // responsibility.
      if (prompt.includes(OUTPUT_PLACEHOLDER)) {
        return prompt.replaceAll(OUTPUT_PLACEHOLDER, () => output);
      }
      // A side-effect command that printed nothing gets its body verbatim —
      // an empty <command_output> block would only tell the model less.
      if (!output.trim()) {
        return prompt;
      }
      return `${prompt}\n\n<command_output>\n${output}\n</command_output>`;
    }

    // Prompt-only commands mirror custom-command semantics: extra args are
    // appended when the body has no {{args}} placeholder.
    if (argsString && !command.body.includes(ARGS_PLACEHOLDER)) {
      return `${prompt}\n${argsString}`;
    }
    return prompt;
  }

  #cwd(): string {
    const workspaceFolders = this.#configService.get().workspaceFolders || [];
    const first = workspaceFolders.find((wsf) => isFileSchemeUri(wsf.uri));
    if (first) {
      return parseURIString(first.uri).fsPath;
    }
    return process.cwd();
  }

  #asError(e: unknown): Error {
    return e instanceof Error ? e : new Error(String(e));
  }
}
